import { useLazyQuery } from "@apollo/client";
import { Box, Button, Chip, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";

import { MEMBER_EXPERIENCE } from "../gql/queries/eventAccess";
import { Spacing } from "../types/enum";

/**
 * "Show what this member can attend" — the controls above the calendar.
 *
 * One set of dropdowns describing a member, and an email box that fills them in
 * from a real one. They stay editable afterwards on purpose: loading a member
 * and then changing only her plan answers "what would she get if she upgraded?",
 * which is the question that actually comes up.
 */

export interface MemberProfile {
  kind: string;
  isVerified: boolean;
  planSlug: string;
  attendedEvents: number;
  /** ISO date; members who joined before 9 Oct 2025 are never locked. */
  joinedAt: string;
}

/**
 * The lock only applies to members who joined after this. There's no control for
 * it — nearly everyone she looks at joined later, and the grandfathered case
 * only matters when a real member is loaded, where her true join date is used.
 */
const LOCK_RELEASE = "2025-10-09";

/** Picking a member kind starts the simulation; ALL means "don't filter". */
export const ALL_MEMBERS = "ALL";

export const DEFAULT_PROFILE: MemberProfile = {
  kind: ALL_MEMBERS,
  isVerified: true,
  planSlug: "Community-Membership-USD-Monthly",
  attendedEvents: 0,
  joinedAt: new Date().toISOString().slice(0, 10),
};

const KINDS = [
  [ALL_MEMBERS, "All members"],
  ["ZERO_TO_ONE", "Little Sister (0–1 mo)"],
  ["ONE_TO_THREE", "1–3 months"],
  ["THREE_TO_TWELVE", "3–12 months"],
  ["MORE_THAN_TWELVE_MONTHS", "12+ months"],
  ["BIG_SISTER", "Big Sister"],
  ["HEY_LADY_TEAM", "Hey Lady Team"],
  ["HEY_LADY_ADMIN", "Hey Lady Admin"],
  ["INACTIVE_MEMBER", "Inactive member"],
];

/**
 * The only plan distinction that changes anything: Social is shut out of coach
 * events, everything else is not.
 *
 * Deliberately two options rather than one per plan. `getSubscriptionTier` maps
 * seven slugs and falls back to Study for anything else, so Community,
 * Foundations and even a member with no plan at all land on the same tier and
 * behave identically — offering them separately would imply a difference that
 * doesn't exist. Supporter is a real tier but two members on beta have it, so
 * it's noise.
 */
const PLANS = [
  ["Community-Membership-USD-Monthly", "Coaching"],
  ["Social-Member-USD-Monthly", "Social"],
];

const ATTENDED = [0, 1, 2, 3, 5, 10];

interface Props {
  profile: MemberProfile;
  onChange: (profile: MemberProfile) => void;
  active: boolean;
  onActiveChange: (active: boolean) => void;
  /** Set when a real member is loaded, so the screen can say whose view it is. */
  loadedName: string | null;
  onLoadedNameChange: (name: string | null) => void;
  summary?: { total: number; canAttend: number; locked: number; blocked: number };
}

const MemberExperienceBar: React.FC<Props> = ({
  profile,
  onChange,
  active,
  onActiveChange,
  loadedName,
  onLoadedNameChange,
  summary,
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [load, { loading }] = useLazyQuery(MEMBER_EXPERIENCE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const m = data?.adminMemberExperience?.member;
      if (!m) {
        return;
      }
      onChange({
        kind: m.kind ?? "ZERO_TO_ONE",
        isVerified: Boolean(m.isVerified),
        planSlug: m.planSlug ?? "",
        attendedEvents: m.attendedEvents ?? 0,
        joinedAt: m.joinedAt
          ? new Date(m.joinedAt).toISOString().slice(0, 10)
          : DEFAULT_PROFILE.joinedAt,
      });
      onLoadedNameChange(m.name || m.email || "that member");
      onActiveChange(true);
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const set = <K extends keyof MemberProfile>(key: K, value: MemberProfile[K]) => {
    const turningOff = key === "kind" && value === ALL_MEMBERS;
    // The rules can't judge "all members" — they need a member. So touching any
    // other filter while the kind is still All settles it on a concrete one
    // rather than guessing silently; she can see which and change it.
    const settleKind = !turningOff && key !== "kind" && profile.kind === ALL_MEMBERS;

    onChange({
      ...profile,
      [key]: value,
      ...(settleKind ? { kind: "ONE_TO_THREE" } : {}),
    });
    // Editing by hand means it's no longer that member's real situation.
    onLoadedNameChange(null);
    // Back to "All members" is how she turns the whole thing off — the calendar
    // goes back to showing every event.
    onActiveChange(!turningOff);
  };

  const field = { minWidth: 150 };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={Spacing.s}
      sx={{ p: Spacing.m, mb: Spacing.m, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 1 }}
    >
      <Box display="flex" alignItems="center" gap={Spacing.s} flexWrap="wrap">
        <Typography variant="caption" color="textSecondary" fontWeight={600}>
          Show what this member can attend:
        </Typography>

        <TextField
          select
          size="small"
          label="Member"
          value={profile.kind}
          onChange={(e) => set("kind", e.target.value)}
          sx={field}
        >
          {KINDS.map(([v, l]) => (
            <MenuItem key={v} value={v}>
              {l}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Verified"
          disabled={!active}
          value={active ? (profile.isVerified ? "yes" : "no") : ALL_MEMBERS}
          onChange={(e) => set("isVerified", e.target.value === "yes")}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value={ALL_MEMBERS}>All</MenuItem>
          <MenuItem value="yes">Verified</MenuItem>
          <MenuItem value="no">Unverified</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Plan"
          disabled={!active}
          value={active ? profile.planSlug : ALL_MEMBERS}
          onChange={(e) => set("planSlug", e.target.value)}
          sx={field}
        >
          <MenuItem value={ALL_MEMBERS}>All</MenuItem>
          {PLANS.map(([v, l]) => (
            <MenuItem key={l} value={v}>
              {l}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title="Three attended events unlock coach, study-group and 45-minute events">
          <TextField
            select
            size="small"
            label="Events attended"
            disabled={!active}
            value={active ? String(profile.attendedEvents) : ALL_MEMBERS}
            onChange={(e) => set("attendedEvents", Number(e.target.value))}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value={ALL_MEMBERS}>All</MenuItem>
            {ATTENDED.map((n) => (
              <MenuItem key={n} value={String(n)}>
                {n === 3 ? "3 (unlocks)" : n}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
      </Box>

      <Box display="flex" alignItems="center" gap={Spacing.s} flexWrap="wrap">
        <Typography variant="caption" color="textSecondary">
          …or load a real member:
        </Typography>
        <TextField
          size="small"
          label="Member email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && email.trim()) {
              void load({ variables: { email: email.trim() } });
            }
          }}
          sx={{ minWidth: 260 }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={loading || !email.trim()}
          onClick={() => void load({ variables: { email: email.trim() } })}
        >
          {loading ? "Loading…" : "Load"}
        </Button>

        {loadedName && <Chip size="small" color="primary" label={`Showing: ${loadedName}`} />}
        {active && (
          <Button
            size="small"
            onClick={() => {
              onActiveChange(false);
              onLoadedNameChange(null);
              onChange(DEFAULT_PROFILE);
              setEmail("");
            }}
          >
            Clear
          </Button>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        {active && summary && (
          <Typography variant="caption" color="textSecondary">
            <strong>{summary.canAttend}</strong> of {summary.total} open · {summary.locked} locked ·{" "}
            {summary.blocked} blocked
          </Typography>
        )}
      </Box>

      {active && (
        <Typography variant="caption" color="textSecondary">
          {loadedName
            ? `These are ${loadedName}'s real settings — change any of them to see what would open up for her.`
            : "Describing a member. Only the events she could attend are shown, so an empty slot is a real gap for her."}
          {profile.joinedAt > LOCK_RELEASE && profile.attendedEvents < 3
            ? ` She's ${3 - profile.attendedEvents} event${3 - profile.attendedEvents === 1 ? "" : "s"} short of unlocking coach, study-group and 45-minute events.`
            : ""}
        </Typography>
      )}
    </Box>
  );
};

export default MemberExperienceBar;
